import { useState } from 'react'

import { Avatar, Box, Divider, Tag, Text } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { Add, Pencil } from '@factorialco/factorial-one-react/icons/app'
import { JoinExternalIntegrationFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { ExternalProfilesModal } from './ExternalProfilesModal'
import { useUpdateIntegrationProfile } from './useUpdateIntegration'

interface Props {
  integrationProfile: JoinExternalIntegrationFragment
  onUpdate: () => void
}

export const ExternalProfilesItem = ({ integrationProfile, onUpdate }: Props) => {
  const { updateIntegrationProfile } = useUpdateIntegrationProfile({ integrationProfile })
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const isEnabled = integrationProfile.status === 'enabled'
  const hasConfiguration = integrationProfile.configuration.length > 0
  const profileAdded = integrationProfile.configuration.every((option) => !!option.value)
  const connected = profileAdded && isEnabled

  const indeedButtonLabel = connected ? i18n.t('common.remove') : i18n.t('common.add')
  const buttonLabel = connected ? i18n.t('common.edit') : i18n.t('common.add')
  const buttonIcon = connected ? Pencil : Add

  const handleUpdate = async (deactivate = false) => {
    const result = await updateIntegrationProfile(deactivate)

    result?.success && onUpdate()
  }

  const handleOnUpdated = () => {
    setIsModalOpen(false)
    onUpdate()
  }

  return (
    <>
      <Box
        gap="s8"
        borderRadius={{
          all: 'abs016',
        }}
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        paddingY="s12"
      >
        <Box paddingX="s16">
          <Avatar label={integrationProfile.slug} url={integrationProfile.logoUrl} />
        </Box>
        <Box gap="s12">
          <Box gap="s4" paddingX="s16">
            <Text.Normal color="grey1000" size="300" weight="semibold">
              {integrationProfile.name}
            </Text.Normal>
            <Text.Normal size="200" ellipsis lineClamp={2}>
              {integrationProfile.description}
            </Text.Normal>
          </Box>
          <>
            <Divider />
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="spaceBetween"
              paddingX="s16"
              minHeight="s32"
            >
              <Tag
                inline
                showCircle
                leftItem="circle"
                type={connected ? 'correct' : 'neutral'}
                size="small"
              >
                {connected
                  ? i18n.t('ats.settings.job_promotions.external_profiles.connected')
                  : i18n.t('ats.settings.job_promotions.external_profiles.disconnected')}
              </Tag>
              {/* // TODO: revisit this when we are able to deactive the rest of profiles */}
              {integrationProfile.name === 'Indeed' ? (
                <Button
                  icon={!connected && Add}
                  variant="outline"
                  label={indeedButtonLabel}
                  onClick={() => handleUpdate(connected ? true : false)}
                />
              ) : (
                <Button
                  icon={buttonIcon}
                  variant="outline"
                  label={buttonLabel}
                  onClick={() => (hasConfiguration ? setIsModalOpen(true) : handleUpdate())}
                />
              )}
            </Box>
          </>
        </Box>
      </Box>

      {isModalOpen && hasConfiguration && (
        <ExternalProfilesModal
          integrationProfile={integrationProfile}
          onUpdated={handleOnUpdated}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  )
}
